import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from './use-toast';

export const DESKTOP_NOTIFICATION_PREF_KEY = 'desktop_message_notifications_enabled';

export const isDesktopNotificationSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

export const getDesktopNotificationPreference = () => {
  if (typeof window === 'undefined') return true;
  const stored = window.localStorage.getItem(DESKTOP_NOTIFICATION_PREF_KEY);
  return stored !== 'false';
};

export const setDesktopNotificationPreference = (enabled) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(DESKTOP_NOTIFICATION_PREF_KEY, enabled ? 'true' : 'false');
};

export const requestDesktopNotificationPermission = async () => {
  if (!isDesktopNotificationSupported()) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';

  try {
    return await Notification.requestPermission();
  } catch (error) {
    console.error('Failed to request desktop notification permission:', error);
    return 'denied';
  }
};

const getMessagePreview = (message = '') => {
  if (!message) return '(No message content)';
  return message.length > 120 ? `${message.slice(0, 120)}...` : message;
};

export const useDesktopMessageNotifications = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { unreadMessages } = useSelector((state) => state.messaging);

  const initializedRef = useRef(false);
  const seenIdsRef = useRef(new Set());

  useEffect(() => {
    if (!isAuthenticated || !Array.isArray(unreadMessages)) return;

    if (!initializedRef.current) {
      unreadMessages.forEach((msg) => {
        if (msg?.id) seenIdsRef.current.add(msg.id);
      });
      initializedRef.current = true;
      return;
    }

    const newUnreadMessages = unreadMessages.filter((msg) => msg?.id && !seenIdsRef.current.has(msg.id));
    if (newUnreadMessages.length === 0) return;

    newUnreadMessages.forEach((msg) => {
      if (msg?.id) seenIdsRef.current.add(msg.id);
    });

    const desktopNotificationsEnabled = getDesktopNotificationPreference();
    const canUseDesktopNotification =
      desktopNotificationsEnabled &&
      isDesktopNotificationSupported() &&
      Notification.permission === 'granted' &&
      document.hidden;

     const displayCount = Math.min(3, newUnreadMessages.length);
     for (let i = 0; i < displayCount; i += 1) {
       const msg = newUnreadMessages[i];
       const senderName = msg?.senderName || 'Unknown Sender';
       const groupName = msg?.groupName || null;
       const preview = getMessagePreview(msg?.body || '');
       const title = groupName
         ? `New message in ${groupName} from ${senderName}`
         : `New message from ${senderName}`;

       toast({
         title,
         description: preview,
         duration: 5000,
       });

       if (canUseDesktopNotification) {
         // Browser native desktop notification (shown when tab/window is in background)
         new Notification(title, {
           body: preview,
           tag: `message-${msg.id}`,
           silent: false,
         });
       }
     }

    if (newUnreadMessages.length > displayCount) {
      toast({
        title: `${newUnreadMessages.length} new messages`,
        description: 'Open Notifications to review all unread messages.',
        duration: 5000,
      });
    }
  }, [isAuthenticated, unreadMessages]);
};

export default useDesktopMessageNotifications;
